import { useEffect, useState } from "react";
import { AppointmentJoin } from "@/repositories/appointments.repository";
import { fetchIndividualAppointment } from "./appointments.api";
import { AppointmentPhotoRepository } from "@/repositories/appointment-photos.repository";
import { AppointmentChecklistRepository } from "@/repositories/appointment-checklist.repository";
import { toast } from "sonner";
import { extractPhotoMetadata } from "@/lib/photo-metadata";
import { appointment_status, AppointmentClockData } from "@/models/appointment.model";
import { useAppointmentChecklist } from "@/hooks/useAppointmentChecklist";
import { clockPhoto, isIncompleteChecklist, isWrongStatus } from "@/lib/appointment-field-actions";
import { toAppointmentJoin } from "@/repositories/appointment-records";
import { appointmentFieldActions, reportFieldActionRefusal, reportPhotoRefusal, reportTickRefusal, reportUploadFailure } from "@/services/appointment-field-actions.service";
import { getMissingWorkflows, WORKFLOWS_NOT_LOADED_MESSAGE } from "@/lib/utils/workflow-photo-gate";

async function getGeolocation(): Promise<GeolocationPosition | null> {
  if (!navigator.geolocation) return null;

  try {
    return await new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, {
        timeout: 5000,
        maximumAge: 60000,
      });
    });
  } catch (error) {
    console.warn("Geolocation failed:", error);
    return null;
  }
}

export const useIndividualAppointmentUpdate = (id: string) => {
  const [loading, setLoading] = useState(true);
  const [appointment, setAppointment] = useState<AppointmentJoin | null>(null);
  const [error, setError] = useState<string | null>(null);

  const {
    serviceTasks,
    serviceWorkflows,
    appointmentTasks,
    showTickAnswer,
    appointmentWorkflows,
    setAppointmentWorkflows,
    checklistLoaded,
    workflowsLoaded,
    checklistError,
    retryChecklist,
    checklistProgress,
    checklistGateError,
  } = useAppointmentChecklist(id, appointment?.service?.id, !loading && !!appointment);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);

        const appt = await fetchIndividualAppointment(id);
        if (!cancelled) setAppointment(appt);
      } catch (e: any) {
        if (!cancelled) setError(e.message ?? "Failed to load appointment");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const updateTaskStatus = async (taskId: string, done: boolean) => {
    try {
      showTickAnswer(await AppointmentChecklistRepository.tick(id, taskId, done));
      toast.success(done ? "Task marked as completed" : "Task marked as incomplete");
    } catch (err) {
      await reportTickRefusal(err, reReadAfterRefusedTick);
    }
  };

  const uploadPhoto = async (workflowId: string, file: File, isAfter: boolean, options?: { isCapture?: boolean }) => {
    try {
      const metadata = await extractPhotoMetadata(file, { isCapture: options?.isCapture });
      setAppointmentWorkflows(await AppointmentPhotoRepository.recordSlotPhoto(id, { workflow: workflowId, isAfter, file, metadata }));
      toast.success(`${isAfter ? "After" : "Before"} photo uploaded`);
    } catch (err) {
      await reportPhotoRefusal(err, reReadAfterRefusedPhoto);
      throw err;
    }
  };

  const reReadAppointment = async () => setAppointment(await fetchIndividualAppointment(id));

  const reReadAfterRefusedPhoto = async () => {
    const [, photos] = await Promise.all([reReadAppointment(), AppointmentPhotoRepository.read(id)]);
    setAppointmentWorkflows(photos);
  };

  const reReadAfterRefusedTick = async () => {
    retryChecklist();
    await reReadAppointment();
  };

  const completeClockIn = async (photoUrl: string) => {
    try {
      if (appointment?.status !== appointment_status.accepted && appointment?.status !== appointment_status.goback) {
        toast.error("You can only start work when the appointment is in accepted or go back status");
        return;
      }

      const position = await getGeolocation();
      const started = await appointmentFieldActions.start(id, clockPhoto(photoUrl, position));
      setAppointment(toAppointmentJoin(started));

      toast.success("Work started. Review your before photos, then tap Begin Tasks.");
    } catch (err) {
      await reportFieldActionRefusal(err, "Failed to start work", reReadAppointment);
    }
  };

  const startWork = async () => {
    try {
      if (appointment?.status !== appointment_status.inprogress) {
        toast.error("Start work before beginning tasks");
        return;
      }

      if (appointment.meta_data?.work_started_at) return;

      if (!workflowsLoaded) {
        toast.error(WORKFLOWS_NOT_LOADED_MESSAGE);
        return;
      }

      if (getMissingWorkflows(serviceWorkflows, appointmentWorkflows, false).length > 0) {
        toast.error("Upload all before photos before beginning tasks.");
        return;
      }

      setAppointment(toAppointmentJoin(await appointmentFieldActions.beginTasks(id)));

      toast.success("Tasks begun — before photos are locked and the checklist is unlocked");
    } catch (err) {
      await reportFieldActionRefusal(err, "Failed to begin tasks", reReadAppointment);
    }
  };

  const addExtraPhoto = async (file: File, isAfter: boolean) => {
    if (appointment?.status !== appointment_status.inprogress) {
      toast.error("Additional photos can only be added while work is in progress");
      return;
    }

    const photoUrl = await AppointmentPhotoRepository.upload(id, isAfter ? "extraAfter" : "extraBefore", file).catch(reportUploadFailure);
    if (!photoUrl) return;

    try {
      setAppointment(toAppointmentJoin(await appointmentFieldActions.addExtraPhoto(id, photoUrl, isAfter)));
      toast.success(`Additional ${isAfter ? "after" : "before"} photo added`);
    } catch (err) {
      await reportFieldActionRefusal(err, "Failed to add photo", reReadAppointment);
    }
  };

  const removeExtraPhoto = async (url: string) => {
    if (appointment?.status !== appointment_status.inprogress) {
      toast.error("Additional photos can only be removed while work is in progress");
      return;
    }

    try {
      setAppointment(toAppointmentJoin(await appointmentFieldActions.removeExtraPhoto(id, url)));
      toast.success("Photo removed");
    } catch (err) {
      await reportFieldActionRefusal(err, "Failed to remove photo", reReadAppointment);
    }
  };

  const completeClockOut = async (photoUrl: string) => {
    if (appointment?.status !== appointment_status.inprogress) {
      const message = "Work can only be finished when the appointment is in progress";
      toast.error(message);
      throw new Error(message);
    }

    if (!workflowsLoaded) {
      toast.error(WORKFLOWS_NOT_LOADED_MESSAGE);
      throw new Error(WORKFLOWS_NOT_LOADED_MESSAGE);
    }

    const missingBefore = getMissingWorkflows(serviceWorkflows, appointmentWorkflows, false);
    if (missingBefore.length > 0) {
      const message = "Upload all before photos before completing work.";
      toast.error(message);
      throw new Error(message);
    }

    if (!appointment.meta_data?.work_started_at) {
      const message = "Tap Begin Tasks to lock before photos before completing work.";
      toast.error(message);
      throw new Error(message);
    }

    if (checklistGateError) {
      toast.error(checklistGateError);
      throw new Error(checklistGateError);
    }

    const missingAfter = getMissingWorkflows(serviceWorkflows, appointmentWorkflows, true);
    if (missingAfter.length > 0) {
      const message = "Upload all after photos before completing work.";
      toast.error(message);
      throw new Error(message);
    }

    try {
      const position = await getGeolocation();
      const finished = await appointmentFieldActions.finish(id, clockPhoto(photoUrl, position));
      setAppointment(toAppointmentJoin(finished));

      const clockInData = appointment?.meta_data?.clock_in as AppointmentClockData | undefined;
      if (clockInData?.time) {
        const clockInTime = new Date(clockInData.time).getTime();
        const durationMs = Date.now() - clockInTime;
        const hours = Math.floor(durationMs / (1000 * 60 * 60));
        const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));
        toast.success(`Work completed. Duration: ${hours}h ${minutes}m`);
      } else {
        toast.success("Work completed successfully");
      }
    } catch (err) {
      await reportFieldActionRefusal(err, "Failed to complete work", reReadAppointment);
      if (isIncompleteChecklist(err)) retryChecklist();
      if (!isWrongStatus(err)) throw err;
    }
  };

  return {
    loading,
    appointment,
    setAppointment,
    serviceTasks,
    serviceWorkflows,
    appointmentTasks,
    appointmentWorkflows,
    checklistLoaded,
    workflowsLoaded,
    checklistError,
    retryChecklist,
    checklistProgress,
    checklistGateError,
    error,
    updateTaskStatus,
    uploadPhoto,
    completeClockIn,
    startWork,
    addExtraPhoto,
    removeExtraPhoto,
    completeClockOut,
  };
};
